import React, { forwardRef, useImperativeHandle } from "react";
import { Box, Typography } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  pdfSliceAndSave,
  injectPageBreakSpacers,
  removePageBreakSpacers,
} from "./pdfUtils";
import { Chart as ChartJS } from "chart.js";

const GcchfHealthReportPdf = forwardRef(
  (
    {
      spiderData = { labels: [], values: [], raw: [] },
      sections = [],
      userName,
      font = "Gotham, sans-serif",
    },
    ref,
  ) => {
    const trimCanvasWhitespace = (src) => {
      const ctx = src.getContext("2d");
      const { data, width, height } = ctx.getImageData(
        0,
        0,
        src.width,
        src.height,
      );
      let top = height,
        bottom = 0,
        left = width,
        right = 0;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const idx = (y * width + x) * 4;
          if (
            data[idx + 3] > 10 &&
            (data[idx] < 245 || data[idx + 1] < 245 || data[idx + 2] < 245)
          ) {
            if (y < top) top = y;
            if (y > bottom) bottom = y;
            if (x < left) left = x;
            if (x > right) right = x;
          }
        }
      }
      const pad = 12;
      top = Math.max(0, top - pad);
      bottom = Math.min(height - 1, bottom + pad);
      left = Math.max(0, left - pad);
      right = Math.min(width - 1, right + pad);
      const tw = right - left + 1;
      const th = bottom - top + 1;
      const out = document.createElement("canvas");
      out.width = tw;
      out.height = th;
      out.getContext("2d").drawImage(src, left, top, tw, th, 0, 0, tw, th);
      return out;
    };

    const drawScoreBadge = (scoreText) => {
      const c = document.createElement("canvas");
      c.width = 200;
      c.height = 100;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#FFCC32";
      ctx.beginPath();
      ctx.roundRect(0, 0, 200, 100, 10);
      ctx.fill();
      ctx.fillStyle = "#263842";
      ctx.font = "bold 54px Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(scoreText, 100, 62);
      ctx.fillStyle = "#50545D";
      ctx.font = "18px Arial, sans-serif";
      ctx.fillText("out of 5.0", 100, 86);
      return c.toDataURL("image/png");
    };

    useImperativeHandle(ref, () => ({
      download: async () => {
        const gcchfScore =
          spiderData.values.length > 0
            ? (
                spiderData.values.reduce((a, b) => a + b, 0) /
                spiderData.values.length
              ).toFixed(1)
            : sections.length > 0
              ? (
                  sections.reduce((a, s) => a + s.score, 0) / sections.length
                ).toFixed(1)
              : "—";

        const scoreBadgeEl = document.getElementById("pdf-score-badge-img");
        if (scoreBadgeEl) {
          scoreBadgeEl.src = drawScoreBadge(gcchfScore);
          scoreBadgeEl.style.display = "block";
        }

        const chartCanvas = document.querySelector("#spider-chart canvas");
        const chartImgEl = document.getElementById("pdf-spider-chart-img");
        if (chartCanvas && chartImgEl) {
          // Temporarily replace labels with numbers for a clean PDF chart
          const radarChart = Object.values(ChartJS.instances).find(
            (c) => c.config.type === "radar",
          );
          let originalLabels = null;
          if (radarChart) {
            originalLabels = [...radarChart.data.labels];
            radarChart.data.labels = originalLabels.map((_, i) =>
              String(i + 1),
            );
            radarChart.update("none");
          }

          const trimmed = trimCanvasWhitespace(chartCanvas);
          chartImgEl.src = trimmed.toDataURL("image/png");
          chartImgEl.style.display = "block";

          // Restore original labels
          if (radarChart && originalLabels) {
            radarChart.data.labels = originalLabels;
            radarChart.update("none");
          }
        }

        const el = document.getElementById("health-report-content");
        if (!el) return;

        const SCALE = 1.5;
        const margins = { top: 20, bottom: 20, left: 20, right: 20 };

        // Calculate one A4 page height in DOM pixels at this scale
        const A4_WIDTH_MM = 210;
        const A4_HEIGHT_MM = 297;
        const imgWidthMm = A4_WIDTH_MM - margins.left - margins.right; // 170 mm
        const canvasWidthPx = el.offsetWidth * SCALE;
        const maxPageHeightCanvas =
          ((A4_HEIGHT_MM - margins.top - margins.bottom) * canvasWidthPx) /
          imgWidthMm;
        const pageHeightDomPx = maxPageHeightCanvas / SCALE;

        // Push any block that straddles a page boundary onto the next page
        injectPageBreakSpacers(el, pageHeightDomPx);

        // Let the browser reflow the inserted spacers before capture
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const canvas = await html2canvas(el, {
          scale: SCALE,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Restore DOM — spacers are no longer needed after the canvas is captured
        removePageBreakSpacers(el);

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        pdfSliceAndSave(canvas, pdf, margins, "GCCHF_Health_Report.pdf");
      },
    }));

    return (
      <Box
        id="health-report-content"
        sx={{
          position: "fixed",
          top: 0,
          left: "200vw",
          zIndex: -9999,
          width: 1100,
          overflow: "hidden",
          bgcolor: "#ffffff",
          fontFamily: font,
        }}
      >
        {/* Cover Header */}
        <Box sx={{ bgcolor: "#263842", px: 4, py: 3.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* LEFT SECTION */}
            <Box>
              <Typography
                sx={{
                  color: "#009CCC",
                  fontSize: 18,
                  fontWeight: 700,
                  fontFamily: font,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  mb: 0.5,
                }}
              >
                City to City Asia Pacific
              </Typography>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: font,
                  lineHeight: 1.2,
                }}
              >
                The Gospel and Church Health Framework Church Health Report
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    color: "#9AA4AF",
                    fontSize: 16,
                    fontFamily: font,
                  }}
                >
                  {userName ? userName : "Church Health Report"}
                </Typography>
                <Typography
                  sx={{
                    color: "#9AA4AF",
                    fontSize: 18,
                    fontFamily: font,
                  }}
                >
                  |
                </Typography>
                <Typography
                  sx={{
                    color: "#9AA4AF",
                    fontSize: 18,
                    fontFamily: font,
                  }}
                >
                  {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT SECTION */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mt: 2,
              }}
            >
              <img
                id="pdf-score-badge-img"
                alt="Overall Score"
                style={{ display: "none", width: 150, height: "auto" }}
              />
              <Typography
                sx={{
                  color: "#9AA4AF",
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: font,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mt: 0.5,
                }}
              >
                Overall Score
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Score Band */}
        <Box
          sx={{
            bgcolor: "#f8fafc",
            borderBottom: "1px solid #C4C4C4",
            px: 4,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {[
            { range: "4.0 – 5.0", label: "Thriving", color: "#263842" },
            { range: "3.0 – 3.9", label: "Growing", color: "#009CCC" },
            { range: "2.0 – 2.9", label: "Developing", color: "#50545D" },
            { range: "0 – 1.9", label: "Still Forming", color: "#ED1C2E" },
          ].map((band) => (
            <Box
              key={band.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Dot */}
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: band.color,
                  flexShrink: 0,
                }}
              />

              {/* ONE text line (important) */}
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 13,
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Box component="span" sx={{ display: "flex", gap: 0.5 }}>
                  <Typography
                    component="span" // ✅ FIX
                    sx={{ fontSize: 11, color: band.color }}
                  >
                    {band.label}
                  </Typography>

                  <Typography
                    component="span" // ✅ FIX
                    sx={{ fontSize: 11, color: "#50545D" }}
                  >
                    {band.range}
                  </Typography>
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography
          sx={{
            fontFamily: font,
            fontSize: 16,
            color: "#263842",
            mt: 3,
            mb: 1,
            ml: 4,
            borderLeft: "4px solid #ED1C2E",
            pl: 1.5,
          }}
        >
          We would encourage you to walk through The Gospel and Church Health
          Framework along with your leaders every six months.
        </Typography>
        <Box sx={{ px: 4, py: 3 }}>
          {/* Spider Chart */}
          <Box
            data-pdf-block=""
            sx={{
              bgcolor: "#fff",
              border: "1px solid #C4C4C4",
              borderRadius: 2,
              p: 2.5,
              mb: 3,
              display: "flex",
              gap: 3,
              alignItems: "stretch",
            }}
          >
            <Box sx={{ flex: "0 0 420px" }}>
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#9AA4AF",
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Health Radar Chart
              </Typography>
              <img
                id="pdf-spider-chart-img"
                alt="Spider Chart"
                style={{ display: "none", width: "100%", height: "auto" }}
              />
            </Box>

            {/* Right column: subsection labels (top) + Gospel Assurance (bottom) */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                pt: 3.5,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* Subsection labels */}
              <Box>
                {spiderData.labels.map((label, i) => {
                  const rawEntry = spiderData.raw[i];
                  const sectionColor =
                    sections.find((s) => s.name === rawEntry?.section_name)
                      ?.color || "#9AA4AF";
                  return (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          bgcolor: sectionColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: font,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#fff",
                            lineHeight: 1,
                          }}
                        >
                          {i + 1}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 14,
                          color: "#50545D",
                          flex: 1,
                        }}
                      >
                        {label}
                      </Typography>
                      <Box
                        sx={{
                          width: 70,
                          height: 5,
                          bgcolor: "#C4C4C4",
                          borderRadius: 2,
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${((spiderData.values[i] || 0) / 5) * 100}%`,
                            bgcolor: sectionColor,
                            borderRadius: 2,
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#263842",
                          width: 28,
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {typeof spiderData.values[i] === "number"
                          ? spiderData.values[i].toFixed(1)
                          : "—"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Gospel Assurance — pinned to the bottom of the right column */}
              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#263842",
                    mb: 0.6,
                    lineHeight: 1.5,
                  }}
                >
                  Gospel Assurance: Our Sonship and security in Christ shapes
                  how we view ministry performance.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#263842",
                    mb: 0.6,
                    lineHeight: 1.5,
                  }}
                >
                  We can celebrate success without pride or self-reliance.
                  <br />
                  We can acknowledge shortcomings without shame.
                  <br />
                  We can labour with all our strength and yet, at the same time,
                  also rest in Christ.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Detailed Section Insights */}
          <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />
          <Box data-pdf-block="">
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 18,
                fontWeight: 700,
                color: "#263842",
                mb: 0.4,
                borderLeft: "4px solid #ED1C2E",
                pl: 1.5,
              }}
            >
              Detailed Section Insights
            </Typography>
            <Typography
              sx={{ fontFamily: font, fontSize: 14, color: "#9AA4AF", mb: 2 }}
            >
              The following provides an in-depth look at each health dimension
              assessed, with specific observations and targeted guidance.
            </Typography>
          </Box>

          {sections.map((s) => {
            const subsections = spiderData.labels
              .map((label, i) => ({
                label,
                value: spiderData.values[i],
                section_name: spiderData.raw[i]?.section_name,
              }))
              .filter((item) => item.section_name === s.name);
            const strengthMsg =
              s.score >= 4
                ? "This area reflects strong health indicators. Continue reinforcing these practices and use them as a model for other areas."
                : s.score >= 3
                  ? "Solid foundations are in place. Focused effort on key sub-areas will elevate this dimension to thriving health."
                  : "This area requires intentional attention. Prioritise equipping leaders and building consistent practices in this dimension.";
            return (
              <Box
                key={s.name}
                data-pdf-block=""
                sx={{
                  mb: 2.5,
                  bgcolor: "#f8fafc",
                  border: "1px solid #C4C4C4",
                  borderRadius: 2,
                  p: 2.5,
                  borderLeft: `4px solid ${s.color}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "3px",
                        bgcolor: s.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 17,
                        fontWeight: 700,
                        color: "#263842",
                      }}
                    >
                      {s.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        bgcolor:
                          s.score >= 4
                            ? "#263842"
                            : s.score >= 3
                              ? "#009CCC"
                              : "#ED1C2E",
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {s.score >= 4
                          ? "Thriving"
                          : s.score >= 3
                            ? "Growing"
                            : "Needs Focus"}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 17,
                        fontWeight: 700,
                        color: "#263842",
                      }}
                    >
                      {s.score} / 5
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 1.5, mt: 0.5 }}>
                  <Box
                    sx={{
                      height: 7,
                      bgcolor: "#C4C4C4",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${((s.score || 0) / 5) * 100}%`,
                        bgcolor: s.color,
                        borderRadius: 3,
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 14,
                    color: "#50545D",
                    lineHeight: 1.8,
                    mb: subsections.length > 0 ? 1.5 : 0,
                  }}
                >
                  {strengthMsg}
                </Typography>
                {subsections.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#9AA4AF",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        mb: 0.8,
                      }}
                    >
                      Subsection Scores
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                      {subsections.map((sub) => (
                        <Box
                          key={sub.label}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            bgcolor: "#fff",
                            border: `1px solid ${s.color}`,
                            borderRadius: 1,
                            px: 1.2,
                            py: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 13,
                              color: "#50545D",
                            }}
                          >
                            {sub.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 13,
                              fontWeight: 700,
                              color: s.color,
                            }}
                          >
                            {typeof sub.value === "number"
                              ? sub.value.toFixed(1)
                              : "—"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}

          {/* Recommendations */}
          <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />
          <Box data-pdf-block="">
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 18,
                fontWeight: 700,
                color: "#263842",
                mb: 0.4,
                borderLeft: "4px solid #ED1C2E",
                pl: 1.5,
              }}
            >
              Recommended Next Steps
            </Typography>
            <Typography
              sx={{ fontFamily: font, fontSize: 14, color: "#9AA4AF", mb: 2 }}
            >
              Based on your results, the following priorities are recommended
              for your church's health journey.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {[...sections]
              .sort((a, b) => a.score - b.score)
              .slice(0, 3)
              .map((s, i) => (
                <Box
                  key={s.name}
                  data-pdf-block=""
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      bgcolor: "#009CCC",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {i + 1}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#263842",
                        mb: 0.2,
                      }}
                    >
                      Focus on{" "}
                      <Box component="span" sx={{ color: s.color }}>
                        {s.name}
                      </Box>{" "}
                      (Score: {s.score}/5)
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 14,
                        color: "#50545D",
                        lineHeight: 1.5,
                      }}
                    >
                      {s.score < 3
                        ? `Prioritise building strong foundational practices in ${s.name}. Consider dedicated leadership development and structured programming in this area.`
                        : `Deepen existing strengths in ${s.name} by decentralising responsibility and empowering more members to lead within this dimension.`}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Box>

          {/* Score Interpretation Table */}
          <Box sx={{ mt: 3, mb: 3 }}>
            <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />
            <Box data-pdf-block="">
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#263842",
                  mb: 0.4,
                  borderLeft: "4px solid #ED1C2E",
                  pl: 1.5,
                }}
              >
                Score Interpretation Guide
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 1.5,
                border: "1px solid #C4C4C4",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {[
                {
                  range: "4.0 – 5.0",
                  label: "Thriving",
                  color: "#fff",
                  bg: "#263842",
                  desc: "The church Is flourishing in the Gospel! Are you praying and planning about planting daughter churches? Or how can you bless your city or the Gospel movement in your city more?.",
                },
                {
                  range: "3.0 – 3.9",
                  label: "Growing",
                  color: "#fff",
                  bg: "#009CCC",
                  desc: "What are some wins you can celebrate with the congregation? What area of growth is the Holy Spirit drawing your attention to?",
                },
                {
                  range: "2.0 – 2.9",
                  label: "Developing",
                  color: "#263842",
                  bg: "#FFCC32",
                  desc: " How can you develop a deeper Gospel Culture that strengthens every area of ministry? Are there other Gosepl-centered churches/leaders that you can consult and brainstorm with?",
                },
                {
                  range: "0 – 1.9",
                  label: "Still Forming",
                  color: "#fff",
                  bg: "#ED1C2E",
                  desc: "Every tree starts oﬀ as a sapling. How can you keep nurturing your heart and the church well? Is there an incubator cohort in your city that you can plug into?",
                },
              ].map((row, i) => (
                <Box
                  key={row.label}
                  data-pdf-block=""
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    borderTop: i === 0 ? "none" : "1px solid #C4C4C4",
                  }}
                >
                  <Box
                    sx={{
                      width: 110,
                      bgcolor: row.bg,
                      px: 1.5,
                      py: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 14,
                        fontWeight: 700,
                        color: row.color,
                      }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 13,
                        color: row.color,
                        opacity: 0.8,
                      }}
                    >
                      {row.range}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, px: 2, py: 1.5 }}>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 14,
                        color: "#50545D",
                        lineHeight: 1.6,
                      }}
                    >
                      {row.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: "#263842",
            px: 4,
            py: 2.5,
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* LEFT */}
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#9AA4AF",
              flex: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.4,
            }}
          >
            City to City Asia Pacific - The Gospel and Church Health Framework
          </Typography>

          {/* RIGHT */}
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#009CCC",
              textAlign: "right",
              whiteSpace: "nowrap",
              lineHeight: 1.4,
            }}
          >
            For coaching and next steps, contact CTCAP
          </Typography>
        </Box>
      </Box>
    );
  },
);

GcchfHealthReportPdf.displayName = "GcchfHealthReportPdf";
export default GcchfHealthReportPdf;
